import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Combobox } from '@/components/ui/combobox';
import { CurrencyInput } from '@/components/ui/currency-input';
import { Label } from '@/components/ui/label';
import { centsToPtbr } from '@/lib/formatters';

interface SimCardTier {
  id: string;
  carriers: string[];
  dataMB: string;
  type: string;
  supplier: string;
  priceWithoutEquipment: string;
  priceInCombo: string;
}

interface SimCardPriceFormProps {
  onPriceChange?: (prices: any) => void;
  initialData?: Array<{
    carriers: string[];
    dataAmountMb: number;
    planType: string;
    provider: string;
    priceWithoutDeviceInCents: number;
    priceInBundleInCents: number;
  }>;
  disabled?: boolean;
}

const carrierOptions = [
  { id: 'Tim', text: 'Tim' },
  { id: 'Claro', text: 'Claro' },
  { id: 'Vivo', text: 'Vivo' },
];

const dataMBOptions = [
  { id: '10MB', text: '10MB' },
  { id: '20MB', text: '20MB' },
  { id: '30MB', text: '30MB' },
];

const typeOptions = [
  { id: 'Compartilhado', text: 'Compartilhado' },
  { id: 'Não compartilhado', text: 'Não compartilhado' },
];

const supplierOptions = [
  { id: 'Arquia Mobile', text: 'Arquia Mobile' },
  { id: 'Emnify', text: 'Emnify' },
  { id: 'Links Field', text: 'Links Field' },
];

export function SimCardPriceForm({
  onPriceChange,
  initialData,
  disabled = false,
}: SimCardPriceFormProps) {
  const [showForm, setShowForm] = useState(!!initialData && initialData.length > 0);
  const [simCardTiers, setSimCardTiers] = useState<SimCardTier[]>(() => {
    if (initialData && initialData.length > 0) {
      return initialData.map((item, index) => ({
        id: (index + 1).toString(),
        carriers: item.carriers || [],
        dataMB: `${item.dataAmountMb}MB`,
        type: item.planType || '',
        supplier: item.provider || '',
        priceWithoutEquipment: centsToPtbr(item.priceWithoutDeviceInCents),
        priceInCombo: centsToPtbr(item.priceInBundleInCents),
      }));
    }
    return [
      {
        id: '1',
        carriers: [],
        dataMB: '',
        type: '',
        supplier: '',
        priceWithoutEquipment: '',
        priceInCombo: '',
      },
    ];
  });

  // Sync state when initialData arrives after mount (e.g. API fetch completes)
  useEffect(() => {
    if (initialData && initialData.length > 0) {
      setShowForm(true);
      setSimCardTiers(
        initialData.map((item, index) => ({
          id: (index + 1).toString(),
          carriers: item.carriers || [],
          dataMB: `${item.dataAmountMb}MB`,
          type: item.planType || '',
          supplier: item.provider || '',
          priceWithoutEquipment: centsToPtbr(item.priceWithoutDeviceInCents),
          priceInCombo: centsToPtbr(item.priceInBundleInCents),
        })),
      );
    }
  }, [initialData]);

  // Track if this is the initial mount to avoid notifying parent on mount
  const isInitialMount = useRef(true);

  // Notify parent when simCardTiers change (after state update)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (onPriceChange) {
      onPriceChange({ simCardTiers });
    }
  }, [simCardTiers, onPriceChange]);

  const updateSimCardTier = (id: string, field: keyof SimCardTier, value: string | string[]) => {
    if (disabled) return;
    setSimCardTiers((prev) =>
      prev.map((tier) => (tier.id === id ? { ...tier, [field]: value } : tier)),
    );
  };

  const removeSimCardTier = (id: string) => {
    if (disabled) return;

    const remaining = simCardTiers.filter((tier) => tier.id !== id);
    if (remaining.length === 0) {
      setShowForm(false);
      setSimCardTiers([
        {
          id: '1',
          carriers: [],
          dataMB: '',
          type: '',
          supplier: '',
          priceWithoutEquipment: '',
          priceInCombo: '',
        },
      ]);
      onPriceChange?.({ simCardTiers: [] });
      return;
    }

    setSimCardTiers(remaining);
  };

  const addSimCardTier = () => {
    if (disabled) return;
    if (!showForm) {
      setShowForm(true);
      return;
    }

    // Add new tier
    const newTier: SimCardTier = {
      id: Date.now().toString(),
      carriers: [],
      dataMB: '',
      type: '',
      supplier: '',
      priceWithoutEquipment: '',
      priceInCombo: '',
    };

    setSimCardTiers((prev) => [...prev, newTier]);
  };

  return (
    <div className="space-y-6 border border-gray-200 rounded-lg p-4">
      <h3 className="font-semibold text-lg text-gray-900">SIM Card</h3>

      {!showForm ? (
        /* Initial Button */
        <Button
          variant="outline"
          onClick={addSimCardTier}
          className="w-full"
          type="button"
          disabled={disabled}
        >
          <Plus className="h-4 w-4 mr-2" />
          Adicionar configuração de SIM Card
        </Button>
      ) : (
        /* SIM Card Pricing Form */
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Configuração de SIM Cards</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-4">
              {/* SIM Card Tiers */}
              {simCardTiers.map((tier) => (
                <div key={tier.id} className="flex flex-col gap-4 p-4 border rounded-lg">
                  {/* SIM Card Configuration */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">Operadora</Label>
                      <Combobox
                        data={carrierOptions}
                        keyExtractor={(item) => item.id}
                        displayValueGetter={(item) => item.text}
                        type="multiple"
                        value={carrierOptions.filter((opt) => tier.carriers.includes(opt.id))}
                        onOptionChange={(value) => {
                          updateSimCardTier(
                            tier.id,
                            'carriers',
                            value.map((v) => v.id),
                          );
                        }}
                        placeholder="Selecione operadoras"
                        disabled={disabled}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm">Quantidade MB</Label>
                      <Combobox
                        data={dataMBOptions}
                        keyExtractor={(item) => item.id}
                        displayValueGetter={(item) => item.text}
                        type="single"
                        value={
                          tier.dataMB ? dataMBOptions.filter((opt) => opt.id === tier.dataMB) : []
                        }
                        onOptionChange={(value) => {
                          updateSimCardTier(tier.id, 'dataMB', value[0]?.id || '');
                        }}
                        placeholder="Selecione quantidade"
                        disabled={disabled}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm">Tipo</Label>
                      <Combobox
                        data={typeOptions}
                        keyExtractor={(item) => item.id}
                        displayValueGetter={(item) => item.text}
                        type="single"
                        value={tier.type ? typeOptions.filter((opt) => opt.id === tier.type) : []}
                        onOptionChange={(value) => {
                          updateSimCardTier(tier.id, 'type', value[0]?.id || '');
                        }}
                        placeholder="Selecione tipo"
                        disabled={disabled}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm">Fornecedor</Label>
                      <Combobox
                        data={supplierOptions}
                        keyExtractor={(item) => item.id}
                        displayValueGetter={(item) => item.text}
                        type="single"
                        value={
                          tier.supplier
                            ? supplierOptions.filter((opt) => opt.id === tier.supplier)
                            : []
                        }
                        onOptionChange={(value) => {
                          updateSimCardTier(tier.id, 'supplier', value[0]?.id || '');
                        }}
                        placeholder="Selecione fornecedor"
                        disabled={disabled}
                      />
                    </div>
                  </div>

                  {/* Price Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">Preço sem equipamento</Label>
                      <CurrencyInput
                        value={tier.priceWithoutEquipment || '0,00'}
                        onChange={(v) => updateSimCardTier(tier.id, 'priceWithoutEquipment', v)}
                        placeholder="0,00"
                        disabled={disabled}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm">Preço no combo</Label>
                      <CurrencyInput
                        value={tier.priceInCombo || '0,00'}
                        onChange={(v) => updateSimCardTier(tier.id, 'priceInCombo', v)}
                        placeholder="0,00"
                        disabled={disabled}
                      />
                    </div>
                  </div>

                  {/* Remove button */}
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => removeSimCardTier(tier.id)}
                      type="button"
                      disabled={disabled}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}

              {/* Add New Configuration Button */}
              <Button
                variant="outline"
                onClick={addSimCardTier}
                className="w-full"
                type="button"
                disabled={disabled}
              >
                <Plus className="h-4 w-4 mr-2" />
                Adicionar configuração
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
